<template>
    <div class="mx-auto" v-if="hasAccess">
        <!-- Global Access Control Settings (Only for Owner/Admin) -->
        <div
            v-if="canManageAccess"
            class="
                bg-white
                shadow-lg
                rounded-lg
                overflow-hidden
                border border-gray-200
                mb-6
                print:hidden
            "
        >
            <div
                class="
                    bg-gray-100
                    px-6
                    py-3
                    border-b
                    flex
                    justify-between
                    items-center
                    cursor-pointer
                    hover:bg-gray-200
                    transition-colors
                "
                @click="isAccessControlOpen = !isAccessControlOpen"
            >
                <div class="flex items-center gap-2">
                    <ion-icon
                        :name="
                            isAccessControlOpen
                                ? 'chevron-down-outline'
                                : 'chevron-forward-outline'
                        "
                        class="text-gray-500"
                    ></ion-icon>
                    <h2 class="text-md font-bold text-gray-800">
                        Global Practice Plan Access Control
                    </h2>
                </div>
                <button
                    @click.stop="saveGlobalAccess"
                    class="
                        bg-primary
                        text-white
                        px-4
                        py-1.5
                        rounded
                        text-xs
                        font-bold
                        hover:bg-primary-dark
                        transition-colors
                        flex
                        items-center
                        gap-1
                    "
                >
                    <ion-icon name="save-outline"></ion-icon>
                    Save Access Settings
                </button>
            </div>
            <div class="px-6 py-4 bg-gray-50" v-show="isAccessControlOpen">
                <p class="text-xs text-gray-500 mb-3">
                    Select the tags required to view and edit Practice Plans. If
                    no tags are selected, all team members can access Practice
                    Plans.
                </p>
                <div class="flex flex-wrap gap-2">
                    <div
                        v-for="tag in availableTags"
                        :key="tag.id"
                        class="
                            text-xs
                            p-2
                            rounded-full
                            inline-flex
                            items-center
                            w-auto
                            gap-2
                            cursor-pointer
                            border
                        "
                        :class="
                            globalActiveTags[tag.id]
                                ? 'opacity-100 border-gray-400 shadow-sm'
                                : 'opacity-50 border-transparent'
                        "
                        :style="{
                            background: tag.color,
                            color: isDark(tag.color) ? '#fff' : '#000',
                        }"
                        @click="
                            globalActiveTags[tag.id] = !globalActiveTags[tag.id]
                        "
                    >
                        <img
                            v-if="tag.icon_type === 'upload'"
                            :src="tag.icon"
                            class="w-[15px] h-[15px]"
                        />
                        <div v-if="tag.icon_type === 'emoji'">
                            {{ tag.icon }}
                        </div>
                        <ion-icon
                            v-if="tag.icon_type === 'ionicons'"
                            :name="tag.icon"
                        ></ion-icon>
                        <span>{{ tag.name }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Calendar 1-Week View -->
        <div
            class="
                bg-white
                shadow-lg
                rounded-lg
                overflow-hidden
                border border-gray-200
                mb-6
                print:hidden
            "
        >
            <div
                class="
                    bg-gray-800
                    text-white
                    px-6
                    py-3
                    flex
                    justify-between
                    items-center
                "
            >
                <button
                    @click="changeWeek(-1)"
                    class="hover:text-primary transition-colors"
                >
                    <ion-icon name="chevron-back" class="text-xl"></ion-icon>
                </button>
                <h2 class="text-lg font-bold">
                    Practice Calendar - Week of {{ weekStartDateFormatted }}
                </h2>
                <button
                    @click="changeWeek(1)"
                    class="hover:text-primary transition-colors"
                >
                    <ion-icon name="chevron-forward" class="text-xl"></ion-icon>
                </button>
            </div>
            <div class="grid grid-cols-7 divide-x border-b">
                <div
                    v-for="(day, dIndex) in weekDays"
                    :key="dIndex"
                    class="text-center p-2 relative transition-colors"
                    :class="{
                        'bg-green-100/60': isSameDay(day.date, practiceDate),
                        'ring-2 ring-primary ring-inset': isToday(day.date),
                    }"
                >
                    <div
                        class="
                            text-xs
                            font-bold
                            uppercase
                            flex
                            items-center
                            justify-center
                            gap-1
                        "
                        :class="
                            isToday(day.date) ? 'text-primary' : 'text-gray-500'
                        "
                    >
                        <ion-icon
                            v-if="isSameDay(day.date, practiceDate)"
                            name="pencil"
                            class="text-green-600 text-[10px]"
                        ></ion-icon>
                        {{ day.name }}
                    </div>
                    <div
                        class="text-lg font-bold mb-2"
                        :class="
                            isToday(day.date) ? 'text-primary' : 'text-gray-800'
                        "
                    >
                        {{ day.dayNum }}
                    </div>

                    <div
                        v-if="day.plan"
                        class="
                            bg-primary/10
                            border border-primary/30
                            rounded
                            p-2
                            text-left text-xs
                            cursor-pointer
                            hover:bg-primary/20
                            transition-colors
                            group
                            relative
                        "
                        @click="loadPlan(day.plan)"
                    >
                        <div
                            class="
                                font-bold
                                text-primary
                                truncate
                                flex
                                items-center
                                justify-center
                                gap-1
                            "
                        >
                            Practice #{{ day.plan.practice_number || "?" }}
                        </div>
                        <div class="text-gray-500 truncate">
                            {{
                                (day.plan.notes || "No notes").substring(0, 20)
                            }}...
                        </div>
                        <button
                            @click.stop="deletePlan(day.plan.id)"
                            class="
                                absolute
                                top-1
                                right-1
                                text-red-400
                                hover:text-red-600
                                opacity-0
                                group-hover:opacity-100
                                transition-opacity
                            "
                        >
                            <ion-icon name="trash"></ion-icon>
                        </button>
                    </div>
                    <div
                        v-else
                        class="
                            text-xs text-gray-400
                            mt-4
                            cursor-pointer
                            hover:text-primary
                        "
                        @click="createNewPlanForDate(day.date)"
                    >
                        + Add Plan
                    </div>
                </div>
            </div>
        </div>

        <!-- Plan Editor -->
        <div
            id="printable-plan"
            class="
                bg-white
                shadow-lg
                rounded-lg
                overflow-hidden
                border border-gray-200
            "
        >
            <div class="p-6 border-b border-gray-200 bg-gray-50">
                <div class="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-800">
                            {{ team.name }} Practice Schedule
                        </h1>
                        <p
                            class="
                                text-sm text-gray-500
                                uppercase
                                tracking-wider
                            "
                        >
                            {{ team.sport_type }}
                        </p>
                        <!-- Connected Collaborators -->
                        <div
                            v-if="connectedUsers.length > 0"
                            class="flex items-center gap-1.5 mt-2 print:hidden"
                        >
                            <span
                                class="
                                    inline-block
                                    w-2.5
                                    h-2.5
                                    rounded-full
                                    bg-green-500
                                    animate-pulse
                                "
                            ></span>
                            <span class="text-xs text-gray-500 font-medium"
                                >Collaborating:</span
                            >
                            <div class="flex -space-x-1 overflow-hidden">
                                <span
                                    v-for="(user, uIdx) in connectedUsers"
                                    :key="uIdx"
                                    class="
                                        inline-block
                                        text-[10px]
                                        font-bold
                                        px-2
                                        py-0.5
                                        rounded-full
                                        border border-white
                                        shadow-sm
                                        text-white
                                    "
                                    :style="{
                                        backgroundColor:
                                            user.color || '#4B5563',
                                    }"
                                    :title="user.name"
                                >
                                    {{ user.name }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="flex gap-4 items-end">
                        <button
                            @click="exportToExcel"
                            class="
                                bg-green-600
                                text-white
                                px-4
                                py-2
                                rounded-md
                                shadow-sm
                                hover:bg-green-700
                                transition-colors
                                font-bold
                                text-sm
                                print:hidden
                            "
                        >
                            <ion-icon
                                name="download-outline"
                                class="text-lg mr-1 align-middle"
                            ></ion-icon>
                            Excel
                        </button>
                        <button
                            @click="printPlan"
                            class="
                                bg-gray-200
                                text-gray-700
                                px-4
                                py-2
                                rounded-md
                                shadow-sm
                                hover:bg-gray-300
                                transition-colors
                                font-bold
                                text-sm
                                print:hidden
                            "
                        >
                            <ion-icon
                                name="print-outline"
                                class="text-lg mr-1 align-middle"
                            ></ion-icon>
                            Print
                        </button>
                        <div class="flex flex-col">
                            <label
                                class="
                                    text-xs
                                    font-bold
                                    text-gray-600
                                    uppercase
                                "
                                >Select Plan</label
                            >
                            <select
                                @change="onPlanSelect($event)"
                                class="
                                    border-gray-300
                                    rounded-md
                                    shadow-sm
                                    focus:border-primary
                                    focus:ring
                                    focus:ring-primary
                                    focus:ring-opacity-50
                                    text-sm
                                "
                            >
                                <option value="">-- New Plan --</option>
                                <option
                                    v-for="p in plansList"
                                    :key="p.id"
                                    :value="p.id"
                                    :selected="currentPlanId === p.id"
                                >
                                    #{{ p.practice_number }} -
                                    {{ p.name || getLocalDateString(p.date) }}
                                    {{ isToday(p.date) ? "(TODAY)" : "" }}
                                </option>
                            </select>
                        </div>
                        <div class="flex flex-col">
                            <label
                                class="
                                    text-xs
                                    font-bold
                                    text-gray-600
                                    uppercase
                                "
                                >Practice #</label
                            >
                            <input
                                v-model="practiceNumber"
                                type="text"
                                class="
                                    border-gray-300
                                    rounded-md
                                    shadow-sm
                                    focus:border-primary
                                    focus:ring
                                    focus:ring-primary
                                    focus:ring-opacity-50
                                "
                                readonly
                            />
                        </div>
                        <div class="flex flex-col">
                            <label
                                class="
                                    text-xs
                                    font-bold
                                    text-gray-600
                                    uppercase
                                "
                                >Plan Name</label
                            >
                            <input
                                v-model="planName"
                                @input="updatePlanName($event.target.value)"
                                @focus="onFieldFocus('planName')"
                                @blur="onFieldBlur"
                                :style="
                                    fieldFocusMap['planName']
                                        ? {
                                              borderColor:
                                                  fieldFocusMap['planName']
                                                      .color,
                                              boxShadow:
                                                  '0 0 0 2px ' +
                                                  fieldFocusMap['planName']
                                                      .color +
                                                  '33',
                                          }
                                        : {}
                                "
                                :title="
                                    fieldFocusMap['planName']
                                        ? fieldFocusMap['planName'].name +
                                          ' is editing this field'
                                        : ''
                                "
                                type="text"
                                class="
                                    border-gray-300
                                    rounded-md
                                    shadow-sm
                                    focus:border-primary
                                    focus:ring
                                    focus:ring-primary
                                    focus:ring-opacity-50
                                "
                                placeholder="Optional name"
                            />
                        </div>
                        <div class="flex flex-col">
                            <label
                                class="text-xs font-bold uppercase"
                                :class="
                                    isToday(practiceDate)
                                        ? 'text-primary'
                                        : 'text-gray-600'
                                "
                                >Date
                                <span
                                    v-if="isToday(practiceDate)"
                                    class="
                                        bg-primary
                                        text-white
                                        px-1.5
                                        py-0.5
                                        rounded-full
                                        text-[9px]
                                        ml-1
                                    "
                                    >TODAY</span
                                >
                            </label>
                            <input
                                v-model="practiceDate"
                                @change="
                                    updatePracticeDate($event.target.value)
                                "
                                @focus="onFieldFocus('practiceDate')"
                                @blur="onFieldBlur"
                                :style="
                                    fieldFocusMap['practiceDate']
                                        ? {
                                              borderColor:
                                                  fieldFocusMap['practiceDate']
                                                      .color,
                                              boxShadow:
                                                  '0 0 0 2px ' +
                                                  fieldFocusMap['practiceDate']
                                                      .color +
                                                  '33',
                                          }
                                        : {}
                                "
                                :title="
                                    fieldFocusMap['practiceDate']
                                        ? fieldFocusMap['practiceDate'].name +
                                          ' is editing this field'
                                        : ''
                                "
                                type="date"
                                class="
                                    border-gray-300
                                    rounded-md
                                    shadow-sm
                                    focus:border-primary
                                    focus:ring
                                    focus:ring-primary
                                    focus:ring-opacity-50
                                "
                                :class="{
                                    'bg-blue-50 text-primary font-bold border-primary ring-1 ring-primary':
                                        isToday(practiceDate),
                                }"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div
                class="
                    px-6
                    py-4
                    bg-white
                    border-b border-gray-200
                    flex
                    justify-between
                    items-center
                    print:hidden
                "
            >
                <div>
                    <button
                        v-if="currentPlanId"
                        @click="deletePlan(currentPlanId)"
                        class="
                            bg-red-500
                            text-white
                            px-4
                            py-2
                            rounded-md
                            shadow-sm
                            hover:bg-red-600
                            transition-colors
                            font-bold
                            text-sm
                        "
                    >
                        Deprecate Plan
                    </button>
                </div>
                <div class="flex gap-2">
                    <button
                        v-if="deprecatedPlans.length > 0"
                        @click="showDeprecatedModal = true"
                        class="
                            bg-gray-500
                            text-white
                            px-4
                            py-2
                            rounded-md
                            shadow-sm
                            hover:bg-gray-600
                            transition-colors
                            font-bold
                            text-sm
                        "
                    >
                        Restore Deprecated ({{ deprecatedPlans.length }})
                    </button>
                </div>
            </div>
            <div class="px-6 py-4 bg-white border-b border-gray-200">
                <label class="text-xs font-bold text-gray-600 uppercase"
                    >Notes: Pre-practice notes/Things to go over</label
                >
                <textarea
                    v-model="notes"
                    @input="updateNotes($event.target.value)"
                    @focus="onFieldFocus('notes')"
                    @blur="onFieldBlur"
                    :style="
                        fieldFocusMap['notes']
                            ? {
                                  borderColor: fieldFocusMap['notes'].color,
                                  boxShadow:
                                      '0 0 0 2px ' +
                                      fieldFocusMap['notes'].color +
                                      '33',
                              }
                            : {}
                    "
                    :title="
                        fieldFocusMap['notes']
                            ? fieldFocusMap['notes'].name +
                              ' is editing this field'
                            : ''
                    "
                    rows="2"
                    class="
                        w-full
                        mt-1
                        border-gray-300
                        rounded-md
                        shadow-sm
                        focus:border-primary
                        focus:ring
                        focus:ring-primary
                        focus:ring-opacity-50
                    "
                    placeholder="Enter notes here..."
                ></textarea>
            </div>

            <!-- Main Table Section -->
            <div class="overflow-x-auto">
                <table class="w-full text-left border-collapse">
                    <thead>
                        <tr
                            class="
                                bg-gray-800
                                text-white text-xs
                                uppercase
                                tracking-wider
                            "
                        >
                            <th class="px-4 py-2 border-r border-gray-700 w-16">
                                PER
                            </th>
                            <th class="px-4 py-2 border-r border-gray-700 w-24">
                                TIME
                            </th>
                            <th class="px-4 py-2 border-r border-gray-700 w-16">
                                MIN
                            </th>
                            <th class="px-4 py-2 border-r border-gray-700 w-32">
                                TYPE
                            </th>
                            <th
                                v-for="col in positionColumns"
                                :key="col"
                                class="
                                    px-4
                                    py-2
                                    border-r border-gray-700
                                    text-center
                                "
                            >
                                {{ col }}
                            </th>
                            <th
                                class="
                                    px-4
                                    py-2
                                    border-r border-gray-700
                                    w-48
                                    text-center
                                "
                            >
                                NOTES
                            </th>
                            <th class="px-4 py-2 w-16 print:hidden"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(period, index) in periods"
                            :key="index"
                            class="
                                border-b border-gray-200
                                hover:bg-gray-50
                                transition-colors
                            "
                        >
                            <td
                                class="px-4 py-2 border-r text-center font-mono"
                            >
                                {{ index + 1 }}
                            </td>
                            <td class="px-4 py-2 border-r">
                                <input
                                    v-model="period.time"
                                    @input="
                                        updatePeriodField(
                                            index,
                                            'time',
                                            $event.target.value
                                        )
                                    "
                                    @focus="
                                        onFieldFocus(
                                            'period-' + index + '-time'
                                        )
                                    "
                                    @blur="onFieldBlur"
                                    :style="
                                        fieldFocusMap[
                                            'period-' + index + '-time'
                                        ]
                                            ? {
                                                  borderColor:
                                                      fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-time'
                                                      ].color,
                                                  boxShadow:
                                                      '0 0 0 2px ' +
                                                      fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-time'
                                                      ].color +
                                                      '33',
                                              }
                                            : {}
                                    "
                                    :title="
                                        fieldFocusMap[
                                            'period-' + index + '-time'
                                        ]
                                            ? fieldFocusMap[
                                                  'period-' + index + '-time'
                                              ].name + ' is editing this field'
                                            : ''
                                    "
                                    type="text"
                                    class="
                                        w-full
                                        border-none
                                        bg-transparent
                                        focus:ring-0
                                        text-sm
                                    "
                                    placeholder="--:--"
                                />
                            </td>
                            <td class="px-4 py-2 border-r text-center">
                                <input
                                    v-model="period.min"
                                    @input="
                                        updatePeriodField(
                                            index,
                                            'min',
                                            parseInt($event.target.value) || 0
                                        )
                                    "
                                    @focus="
                                        onFieldFocus('period-' + index + '-min')
                                    "
                                    @blur="onFieldBlur"
                                    :style="
                                        fieldFocusMap[
                                            'period-' + index + '-min'
                                        ]
                                            ? {
                                                  borderColor:
                                                      fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-min'
                                                      ].color,
                                                  boxShadow:
                                                      '0 0 0 2px ' +
                                                      fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-min'
                                                      ].color +
                                                      '33',
                                              }
                                            : {}
                                    "
                                    :title="
                                        fieldFocusMap[
                                            'period-' + index + '-min'
                                        ]
                                            ? fieldFocusMap[
                                                  'period-' + index + '-min'
                                              ].name + ' is editing this field'
                                            : ''
                                    "
                                    type="number"
                                    class="
                                        w-full
                                        border-none
                                        bg-transparent
                                        focus:ring-0
                                        text-sm text-center
                                    "
                                    placeholder="0"
                                />
                            </td>
                            <td class="px-4 py-2 border-r">
                                <select
                                    v-model="period.type"
                                    @change="
                                        onPeriodTypeChange(
                                            index,
                                            $event.target.value
                                        )
                                    "
                                    @focus="
                                        onFieldFocus(
                                            'period-' + index + '-type'
                                        )
                                    "
                                    @blur="onFieldBlur"
                                    :style="
                                        fieldFocusMap[
                                            'period-' + index + '-type'
                                        ]
                                            ? {
                                                  borderColor:
                                                      fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-type'
                                                      ].color,
                                                  boxShadow:
                                                      '0 0 0 2px ' +
                                                      fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-type'
                                                      ].color +
                                                      '33',
                                              }
                                            : {}
                                    "
                                    :title="
                                        fieldFocusMap[
                                            'period-' + index + '-type'
                                        ]
                                            ? fieldFocusMap[
                                                  'period-' + index + '-type'
                                              ].name + ' is editing this field'
                                            : ''
                                    "
                                    class="
                                        w-full
                                        border-none
                                        bg-transparent
                                        focus:ring-0
                                        text-sm
                                        font-bold
                                    "
                                >
                                    <option
                                        v-for="pt in availablePeriodTypes"
                                        :key="pt.value"
                                        :value="pt.value"
                                    >
                                        {{ pt.label }}
                                    </option>
                                </select>
                            </td>

                            <!-- Expanded Type (Single Cell) -->
                            <template
                                v-if="getPeriodLayout(period.type) === 'expand'"
                            >
                                <td
                                    :colspan="positionColumns.length"
                                    class="
                                        px-4
                                        py-2
                                        border-r
                                        align-top
                                        bg-gray-50
                                    "
                                >
                                    <div class="flex flex-col gap-2 max-w-xl">
                                        <div class="relative">
                                            <select
                                                v-model="
                                                    period.allPositions.drill
                                                "
                                                @change="
                                                    updatePeriodNestedField(
                                                        index,
                                                        'allPositions',
                                                        'drill',
                                                        $event.target.value
                                                    )
                                                "
                                                @focus="
                                                    onFieldFocus(
                                                        'period-' +
                                                            index +
                                                            '-allPositions-drill'
                                                    )
                                                "
                                                @blur="onFieldBlur"
                                                :style="
                                                    fieldFocusMap[
                                                        'period-' +
                                                            index +
                                                            '-allPositions-drill'
                                                    ]
                                                        ? {
                                                              borderColor:
                                                                  fieldFocusMap[
                                                                      'period-' +
                                                                          index +
                                                                          '-allPositions-drill'
                                                                  ].color,
                                                              boxShadow:
                                                                  '0 0 0 2px ' +
                                                                  fieldFocusMap[
                                                                      'period-' +
                                                                          index +
                                                                          '-allPositions-drill'
                                                                  ].color +
                                                                  '33',
                                                          }
                                                        : {}
                                                "
                                                :title="
                                                    fieldFocusMap[
                                                        'period-' +
                                                            index +
                                                            '-allPositions-drill'
                                                    ]
                                                        ? fieldFocusMap[
                                                              'period-' +
                                                                  index +
                                                                  '-allPositions-drill'
                                                          ].name +
                                                          ' is editing this field'
                                                        : ''
                                                "
                                                class="
                                                    w-full
                                                    text-xs
                                                    border border-gray-200
                                                    rounded
                                                    p-1
                                                    bg-white
                                                "
                                            >
                                                <option value="">
                                                    Select Drill
                                                </option>
                                                <option
                                                    v-for="drill in availableDrills"
                                                    :key="drill"
                                                    :value="drill"
                                                >
                                                    {{ drill }}
                                                </option>
                                            </select>
                                            <button
                                                @click="
                                                    promptNewDrill(
                                                        index,
                                                        'allPositions'
                                                    )
                                                "
                                                class="
                                                    text-[10px] text-primary
                                                    mt-1
                                                    hover:underline
                                                    print:hidden
                                                "
                                            >
                                                + Custom Drill
                                            </button>
                                        </div>

                                        <textarea
                                            v-model="
                                                period.allPositions.content
                                            "
                                            @input="
                                                updatePeriodNestedField(
                                                    index,
                                                    'allPositions',
                                                    'content',
                                                    $event.target.value
                                                )
                                            "
                                            @focus="
                                                onFieldFocus(
                                                    'period-' +
                                                        index +
                                                        '-allPositions-content'
                                                )
                                            "
                                            @blur="onFieldBlur"
                                            :style="
                                                fieldFocusMap[
                                                    'period-' +
                                                        index +
                                                        '-allPositions-content'
                                                ]
                                                    ? {
                                                          borderColor:
                                                              fieldFocusMap[
                                                                  'period-' +
                                                                      index +
                                                                      '-allPositions-content'
                                                              ].color,
                                                          boxShadow:
                                                              '0 0 0 2px ' +
                                                              fieldFocusMap[
                                                                  'period-' +
                                                                      index +
                                                                      '-allPositions-content'
                                                              ].color +
                                                              '33',
                                                      }
                                                    : {}
                                            "
                                            :title="
                                                fieldFocusMap[
                                                    'period-' +
                                                        index +
                                                        '-allPositions-content'
                                                ]
                                                    ? fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-allPositions-content'
                                                      ].name +
                                                      ' is editing this field'
                                                    : ''
                                            "
                                            rows="1"
                                            class="
                                                w-full
                                                text-xs
                                                border-none
                                                bg-transparent
                                                focus:ring-0
                                                resize-none
                                                p-0
                                            "
                                            placeholder="Details..."
                                        ></textarea>

                                        <div
                                            class="
                                                flex flex-wrap
                                                gap-1
                                                mt-1
                                                print:hidden
                                            "
                                        >
                                            <div class="relative">
                                                <button
                                                    @click="
                                                        openPlaySelector(
                                                            index,
                                                            'allPositions'
                                                        )
                                                    "
                                                    class="
                                                        flex
                                                        items-center
                                                        gap-1
                                                        text-[10px]
                                                        bg-blue-100
                                                        text-blue-700
                                                        px-1.5
                                                        py-0.5
                                                        rounded
                                                        border border-blue-200
                                                        hover:bg-blue-200
                                                    "
                                                >
                                                    <ion-icon
                                                        name="map-outline"
                                                    ></ion-icon>
                                                    {{
                                                        period.allPositions
                                                            .plays.length || ""
                                                    }}
                                                    Play
                                                </button>
                                            </div>
                                            <div class="relative">
                                                <button
                                                    @click="
                                                        openVideoSelector(
                                                            index,
                                                            'allPositions'
                                                        )
                                                    "
                                                    class="
                                                        flex
                                                        items-center
                                                        gap-1
                                                        text-[10px]
                                                        bg-green-100
                                                        text-green-700
                                                        px-1.5
                                                        py-0.5
                                                        rounded
                                                        border border-green-200
                                                        hover:bg-green-200
                                                    "
                                                >
                                                    <ion-icon
                                                        name="videocam-outline"
                                                    ></ion-icon>
                                                    {{
                                                        period.allPositions
                                                            .videos.length || ""
                                                    }}
                                                    Video
                                                </button>
                                            </div>
                                        </div>

                                        <div class="flex flex-wrap gap-1">
                                            <span
                                                v-for="play in period
                                                    .allPositions.plays"
                                                :key="play.play_id"
                                                class="
                                                    text-[9px]
                                                    bg-blue-50
                                                    text-blue-600
                                                    px-1
                                                    rounded
                                                    border border-blue-100
                                                "
                                                >{{ play.play_name }}</span
                                            >
                                            <span
                                                v-for="video in period
                                                    .allPositions.videos"
                                                :key="video.id"
                                                class="
                                                    text-[9px]
                                                    bg-green-50
                                                    text-green-600
                                                    px-1
                                                    rounded
                                                    border border-green-100
                                                "
                                                >{{ video.title }}</span
                                            >
                                        </div>
                                    </div>
                                </td>
                            </template>

                            <!-- Split Type (Two Cells) -->
                            <template
                                v-else-if="
                                    getPeriodLayout(period.type) === 'split'
                                "
                            >
                                <!-- Group 1 -->
                                <td
                                    :colspan="
                                        getSplitColspans(period.type).group1
                                    "
                                    class="
                                        px-4
                                        py-2
                                        border-r
                                        align-top
                                        bg-blue-50/30
                                    "
                                >
                                    <div
                                        class="
                                            text-[10px]
                                            font-bold
                                            text-gray-500
                                            mb-1
                                            uppercase
                                        "
                                    >
                                        {{ getSplitLabels(period.type).group1 }}
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <div class="relative">
                                            <select
                                                v-model="
                                                    period.splitPositions.group1
                                                        .drill
                                                "
                                                @change="
                                                    updatePeriodSplitField(
                                                        index,
                                                        'group1',
                                                        'drill',
                                                        $event.target.value
                                                    )
                                                "
                                                @focus="
                                                    onFieldFocus(
                                                        'period-' +
                                                            index +
                                                            '-splitPositions-group1-drill'
                                                    )
                                                "
                                                @blur="onFieldBlur"
                                                :style="
                                                    fieldFocusMap[
                                                        'period-' +
                                                            index +
                                                            '-splitPositions-group1-drill'
                                                    ]
                                                        ? {
                                                              borderColor:
                                                                  fieldFocusMap[
                                                                      'period-' +
                                                                          index +
                                                                          '-splitPositions-group1-drill'
                                                                  ].color,
                                                              boxShadow:
                                                                  '0 0 0 2px ' +
                                                                  fieldFocusMap[
                                                                      'period-' +
                                                                          index +
                                                                          '-splitPositions-group1-drill'
                                                                  ].color +
                                                                  '33',
                                                          }
                                                        : {}
                                                "
                                                :title="
                                                    fieldFocusMap[
                                                        'period-' +
                                                            index +
                                                            '-splitPositions-group1-drill'
                                                    ]
                                                        ? fieldFocusMap[
                                                              'period-' +
                                                                  index +
                                                                  '-splitPositions-group1-drill'
                                                          ].name +
                                                          ' is editing this field'
                                                        : ''
                                                "
                                                class="
                                                    w-full
                                                    text-xs
                                                    border border-gray-200
                                                    rounded
                                                    p-1
                                                    bg-white
                                                "
                                            >
                                                <option value="">
                                                    Select Drill
                                                </option>
                                                <option
                                                    v-for="drill in availableDrills"
                                                    :key="drill"
                                                    :value="drill"
                                                >
                                                    {{ drill }}
                                                </option>
                                            </select>
                                            <button
                                                @click="
                                                    promptNewDrill(
                                                        index,
                                                        'splitPositions',
                                                        'group1'
                                                    )
                                                "
                                                class="
                                                    text-[10px] text-primary
                                                    mt-1
                                                    hover:underline
                                                    print:hidden
                                                "
                                            >
                                                + Custom Drill
                                            </button>
                                        </div>
                                        <textarea
                                            v-model="
                                                period.splitPositions.group1
                                                    .content
                                            "
                                            @input="
                                                updatePeriodSplitField(
                                                    index,
                                                    'group1',
                                                    'content',
                                                    $event.target.value
                                                )
                                            "
                                            @focus="
                                                onFieldFocus(
                                                    'period-' +
                                                        index +
                                                        '-splitPositions-group1-content'
                                                )
                                            "
                                            @blur="onFieldBlur"
                                            :style="
                                                fieldFocusMap[
                                                    'period-' +
                                                        index +
                                                        '-splitPositions-group1-content'
                                                ]
                                                    ? {
                                                          borderColor:
                                                              fieldFocusMap[
                                                                  'period-' +
                                                                      index +
                                                                      '-splitPositions-group1-content'
                                                              ].color,
                                                          boxShadow:
                                                              '0 0 0 2px ' +
                                                              fieldFocusMap[
                                                                  'period-' +
                                                                      index +
                                                                      '-splitPositions-group1-content'
                                                              ].color +
                                                              '33',
                                                      }
                                                    : {}
                                            "
                                            :title="
                                                fieldFocusMap[
                                                    'period-' +
                                                        index +
                                                        '-splitPositions-group1-content'
                                                ]
                                                    ? fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-splitPositions-group1-content'
                                                      ].name +
                                                      ' is editing this field'
                                                    : ''
                                            "
                                            rows="1"
                                            class="
                                                w-full
                                                text-xs
                                                border-none
                                                bg-transparent
                                                focus:ring-0
                                                resize-none
                                                p-0
                                            "
                                            placeholder="Details..."
                                        ></textarea>
                                        <div
                                            class="
                                                flex flex-wrap
                                                gap-1
                                                mt-1
                                                print:hidden
                                            "
                                        >
                                            <button
                                                @click="
                                                    openPlaySelector(
                                                        index,
                                                        'splitPositions',
                                                        'group1'
                                                    )
                                                "
                                                class="
                                                    flex
                                                    items-center
                                                    gap-1
                                                    text-[10px]
                                                    bg-blue-100
                                                    text-blue-700
                                                    px-1.5
                                                    py-0.5
                                                    rounded
                                                    border border-blue-200
                                                    hover:bg-blue-200
                                                "
                                            >
                                                <ion-icon
                                                    name="map-outline"
                                                ></ion-icon>
                                                {{
                                                    period.splitPositions.group1
                                                        .plays.length || ""
                                                }}
                                                Play
                                            </button>
                                            <button
                                                @click="
                                                    openVideoSelector(
                                                        index,
                                                        'splitPositions',
                                                        'group1'
                                                    )
                                                "
                                                class="
                                                    flex
                                                    items-center
                                                    gap-1
                                                    text-[10px]
                                                    bg-green-100
                                                    text-green-700
                                                    px-1.5
                                                    py-0.5
                                                    rounded
                                                    border border-green-200
                                                    hover:bg-green-200
                                                "
                                            >
                                                <ion-icon
                                                    name="videocam-outline"
                                                ></ion-icon>
                                                {{
                                                    period.splitPositions.group1
                                                        .videos.length || ""
                                                }}
                                                Video
                                            </button>
                                        </div>
                                        <div class="flex flex-wrap gap-1">
                                            <span
                                                v-for="play in period
                                                    .splitPositions.group1
                                                    .plays"
                                                :key="play.play_id"
                                                class="
                                                    text-[9px]
                                                    bg-blue-50
                                                    text-blue-600
                                                    px-1
                                                    rounded
                                                    border border-blue-100
                                                "
                                                >{{ play.play_name }}</span
                                            >
                                            <span
                                                v-for="video in period
                                                    .splitPositions.group1
                                                    .videos"
                                                :key="video.id"
                                                class="
                                                    text-[9px]
                                                    bg-green-50
                                                    text-green-600
                                                    px-1
                                                    rounded
                                                    border border-green-100
                                                "
                                                >{{ video.title }}</span
                                            >
                                        </div>
                                    </div>
                                </td>
                                <!-- Group 2 -->
                                <td
                                    :colspan="
                                        getSplitColspans(period.type).group2
                                    "
                                    class="
                                        px-4
                                        py-2
                                        border-r
                                        align-top
                                        bg-green-50/30
                                    "
                                >
                                    <div
                                        class="
                                            text-[10px]
                                            font-bold
                                            text-gray-500
                                            mb-1
                                            uppercase
                                        "
                                    >
                                        {{ getSplitLabels(period.type).group2 }}
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <div class="relative">
                                            <select
                                                v-model="
                                                    period.splitPositions.group2
                                                        .drill
                                                "
                                                @change="
                                                    updatePeriodSplitField(
                                                        index,
                                                        'group2',
                                                        'drill',
                                                        $event.target.value
                                                    )
                                                "
                                                @focus="
                                                    onFieldFocus(
                                                        'period-' +
                                                            index +
                                                            '-splitPositions-group2-drill'
                                                    )
                                                "
                                                @blur="onFieldBlur"
                                                :style="
                                                    fieldFocusMap[
                                                        'period-' +
                                                            index +
                                                            '-splitPositions-group2-drill'
                                                    ]
                                                        ? {
                                                              borderColor:
                                                                  fieldFocusMap[
                                                                      'period-' +
                                                                          index +
                                                                          '-splitPositions-group2-drill'
                                                                  ].color,
                                                              boxShadow:
                                                                  '0 0 0 2px ' +
                                                                  fieldFocusMap[
                                                                      'period-' +
                                                                          index +
                                                                          '-splitPositions-group2-drill'
                                                                  ].color +
                                                                  '33',
                                                          }
                                                        : {}
                                                "
                                                :title="
                                                    fieldFocusMap[
                                                        'period-' +
                                                            index +
                                                            '-splitPositions-group2-drill'
                                                    ]
                                                        ? fieldFocusMap[
                                                              'period-' +
                                                                  index +
                                                                  '-splitPositions-group2-drill'
                                                          ].name +
                                                          ' is editing this field'
                                                        : ''
                                                "
                                                class="
                                                    w-full
                                                    text-xs
                                                    border border-gray-200
                                                    rounded
                                                    p-1
                                                    bg-white
                                                "
                                            >
                                                <option value="">
                                                    Select Drill
                                                </option>
                                                <option
                                                    v-for="drill in availableDrills"
                                                    :key="drill"
                                                    :value="drill"
                                                >
                                                    {{ drill }}
                                                </option>
                                            </select>
                                            <button
                                                @click="
                                                    promptNewDrill(
                                                        index,
                                                        'splitPositions',
                                                        'group2'
                                                    )
                                                "
                                                class="
                                                    text-[10px] text-primary
                                                    mt-1
                                                    hover:underline
                                                    print:hidden
                                                "
                                            >
                                                + Custom Drill
                                            </button>
                                        </div>
                                        <textarea
                                            v-model="
                                                period.splitPositions.group2
                                                    .content
                                            "
                                            @input="
                                                updatePeriodSplitField(
                                                    index,
                                                    'group2',
                                                    'content',
                                                    $event.target.value
                                                )
                                            "
                                            @focus="
                                                onFieldFocus(
                                                    'period-' +
                                                        index +
                                                        '-splitPositions-group2-content'
                                                )
                                            "
                                            @blur="onFieldBlur"
                                            :style="
                                                fieldFocusMap[
                                                    'period-' +
                                                        index +
                                                        '-splitPositions-group2-content'
                                                ]
                                                    ? {
                                                          borderColor:
                                                              fieldFocusMap[
                                                                  'period-' +
                                                                      index +
                                                                      '-splitPositions-group2-content'
                                                              ].color,
                                                          boxShadow:
                                                              '0 0 0 2px ' +
                                                              fieldFocusMap[
                                                                  'period-' +
                                                                      index +
                                                                      '-splitPositions-group2-content'
                                                              ].color +
                                                              '33',
                                                      }
                                                    : {}
                                            "
                                            :title="
                                                fieldFocusMap[
                                                    'period-' +
                                                        index +
                                                        '-splitPositions-group2-content'
                                                ]
                                                    ? fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-splitPositions-group2-content'
                                                      ].name +
                                                      ' is editing this field'
                                                    : ''
                                            "
                                            rows="1"
                                            class="
                                                w-full
                                                text-xs
                                                border-none
                                                bg-transparent
                                                focus:ring-0
                                                resize-none
                                                p-0
                                            "
                                            placeholder="Details..."
                                        ></textarea>
                                        <div
                                            class="
                                                flex flex-wrap
                                                gap-1
                                                mt-1
                                                print:hidden
                                            "
                                        >
                                            <button
                                                @click="
                                                    openPlaySelector(
                                                        index,
                                                        'splitPositions',
                                                        'group2'
                                                    )
                                                "
                                                class="
                                                    flex
                                                    items-center
                                                    gap-1
                                                    text-[10px]
                                                    bg-blue-100
                                                    text-blue-700
                                                    px-1.5
                                                    py-0.5
                                                    rounded
                                                    border border-blue-200
                                                    hover:bg-blue-200
                                                "
                                            >
                                                <ion-icon
                                                    name="map-outline"
                                                ></ion-icon>
                                                {{
                                                    period.splitPositions.group2
                                                        .plays.length || ""
                                                }}
                                                Play
                                            </button>
                                            <button
                                                @click="
                                                    openVideoSelector(
                                                        index,
                                                        'splitPositions',
                                                        'group2'
                                                    )
                                                "
                                                class="
                                                    flex
                                                    items-center
                                                    gap-1
                                                    text-[10px]
                                                    bg-green-100
                                                    text-green-700
                                                    px-1.5
                                                    py-0.5
                                                    rounded
                                                    border border-green-200
                                                    hover:bg-green-200
                                                "
                                            >
                                                <ion-icon
                                                    name="videocam-outline"
                                                ></ion-icon>
                                                {{
                                                    period.splitPositions.group2
                                                        .videos.length || ""
                                                }}
                                                Video
                                            </button>
                                        </div>
                                        <div class="flex flex-wrap gap-1">
                                            <span
                                                v-for="play in period
                                                    .splitPositions.group2
                                                    .plays"
                                                :key="play.play_id"
                                                class="
                                                    text-[9px]
                                                    bg-blue-50
                                                    text-blue-600
                                                    px-1
                                                    rounded
                                                    border border-blue-100
                                                "
                                                >{{ play.play_name }}</span
                                            >
                                            <span
                                                v-for="video in period
                                                    .splitPositions.group2
                                                    .videos"
                                                :key="video.id"
                                                class="
                                                    text-[9px]
                                                    bg-green-50
                                                    text-green-600
                                                    px-1
                                                    rounded
                                                    border border-green-100
                                                "
                                                >{{ video.title }}</span
                                            >
                                        </div>
                                    </div>
                                </td>
                            </template>

                            <!-- Standard Type (Multiple Cells) -->
                            <template v-else>
                                <td
                                    v-for="(pos, pIdx) in positionColumns"
                                    :key="pos"
                                    class="px-4 py-2 border-r align-top"
                                >
                                    <div class="flex flex-col gap-2">
                                        <template
                                            v-if="period.type.includes('INDY')"
                                        >
                                            <div class="relative">
                                                <select
                                                    v-model="
                                                        period.positions[pos]
                                                            .drill
                                                    "
                                                    @change="
                                                        updatePeriodPositionField(
                                                            index,
                                                            pos,
                                                            'drill',
                                                            $event.target.value
                                                        )
                                                    "
                                                    @focus="
                                                        onFieldFocus(
                                                            'period-' +
                                                                index +
                                                                '-positions-' +
                                                                pos +
                                                                '-drill'
                                                        )
                                                    "
                                                    @blur="onFieldBlur"
                                                    :style="
                                                        fieldFocusMap[
                                                            'period-' +
                                                                index +
                                                                '-positions-' +
                                                                pos +
                                                                '-drill'
                                                        ]
                                                            ? {
                                                                  borderColor:
                                                                      fieldFocusMap[
                                                                          'period-' +
                                                                              index +
                                                                              '-positions-' +
                                                                              pos +
                                                                              '-drill'
                                                                      ].color,
                                                                  boxShadow:
                                                                      '0 0 0 2px ' +
                                                                      fieldFocusMap[
                                                                          'period-' +
                                                                              index +
                                                                              '-positions-' +
                                                                              pos +
                                                                              '-drill'
                                                                      ].color +
                                                                      '33',
                                                              }
                                                            : {}
                                                    "
                                                    :title="
                                                        fieldFocusMap[
                                                            'period-' +
                                                                index +
                                                                '-positions-' +
                                                                pos +
                                                                '-drill'
                                                        ]
                                                            ? fieldFocusMap[
                                                                  'period-' +
                                                                      index +
                                                                      '-positions-' +
                                                                      pos +
                                                                      '-drill'
                                                              ].name +
                                                              ' is editing this field'
                                                            : ''
                                                    "
                                                    class="
                                                        w-full
                                                        text-xs
                                                        border border-gray-200
                                                        rounded
                                                        p-1
                                                        bg-white
                                                    "
                                                >
                                                    <option value="">
                                                        Select Drill
                                                    </option>
                                                    <option
                                                        v-for="drill in availableDrills"
                                                        :key="drill"
                                                        :value="drill"
                                                    >
                                                        {{ drill }}
                                                    </option>
                                                </select>
                                                <button
                                                    @click="
                                                        promptNewDrill(
                                                            index,
                                                            pos
                                                        )
                                                    "
                                                    class="
                                                        text-[10px] text-primary
                                                        mt-1
                                                        hover:underline
                                                        print:hidden
                                                    "
                                                >
                                                    + Custom Drill
                                                </button>
                                            </div>
                                        </template>

                                        <textarea
                                            v-model="
                                                period.positions[pos].content
                                            "
                                            @input="
                                                updatePeriodPositionField(
                                                    index,
                                                    pos,
                                                    'content',
                                                    $event.target.value
                                                )
                                            "
                                            @focus="
                                                onFieldFocus(
                                                    'period-' +
                                                        index +
                                                        '-positions-' +
                                                        pos +
                                                        '-content'
                                                )
                                            "
                                            @blur="onFieldBlur"
                                            :style="
                                                fieldFocusMap[
                                                    'period-' +
                                                        index +
                                                        '-positions-' +
                                                        pos +
                                                        '-content'
                                                ]
                                                    ? {
                                                          borderColor:
                                                              fieldFocusMap[
                                                                  'period-' +
                                                                      index +
                                                                      '-positions-' +
                                                                      pos +
                                                                      '-content'
                                                              ].color,
                                                          boxShadow:
                                                              '0 0 0 2px ' +
                                                              fieldFocusMap[
                                                                  'period-' +
                                                                      index +
                                                                      '-positions-' +
                                                                      pos +
                                                                      '-content'
                                                              ].color +
                                                              '33',
                                                      }
                                                    : {}
                                            "
                                            :title="
                                                fieldFocusMap[
                                                    'period-' +
                                                        index +
                                                        '-positions-' +
                                                        pos +
                                                        '-content'
                                                ]
                                                    ? fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-positions-' +
                                                              pos +
                                                              '-content'
                                                      ].name +
                                                      ' is editing this field'
                                                    : ''
                                            "
                                            rows="1"
                                            class="
                                                w-full
                                                text-xs
                                                border-none
                                                bg-transparent
                                                focus:ring-0
                                                resize-none
                                                p-0
                                            "
                                            placeholder="Details..."
                                        ></textarea>

                                        <div
                                            class="
                                                flex flex-wrap
                                                gap-1
                                                mt-1
                                                print:hidden
                                            "
                                        >
                                            <div
                                                v-if="
                                                    period.type ===
                                                        'TEAM CALLS' ||
                                                    period.type.includes('INDY')
                                                "
                                                class="relative"
                                            >
                                                <button
                                                    @click="
                                                        openPlaySelector(
                                                            index,
                                                            pos
                                                        )
                                                    "
                                                    class="
                                                        flex
                                                        items-center
                                                        gap-1
                                                        text-[10px]
                                                        bg-blue-100
                                                        text-blue-700
                                                        px-1.5
                                                        py-0.5
                                                        rounded
                                                        border border-blue-200
                                                        hover:bg-blue-200
                                                    "
                                                >
                                                    <ion-icon
                                                        name="map-outline"
                                                    ></ion-icon>
                                                    {{
                                                        period.positions[pos]
                                                            .plays.length || ""
                                                    }}
                                                    Play
                                                </button>
                                            </div>
                                            <div
                                                v-if="
                                                    period.type !== 'TEAM CALLS'
                                                "
                                                class="relative"
                                            >
                                                <button
                                                    @click="
                                                        openVideoSelector(
                                                            index,
                                                            pos
                                                        )
                                                    "
                                                    class="
                                                        flex
                                                        items-center
                                                        gap-1
                                                        text-[10px]
                                                        bg-green-100
                                                        text-green-700
                                                        px-1.5
                                                        py-0.5
                                                        rounded
                                                        border border-green-200
                                                        hover:bg-green-200
                                                    "
                                                >
                                                    <ion-icon
                                                        name="videocam-outline"
                                                    ></ion-icon>
                                                    {{
                                                        period.positions[pos]
                                                            .videos.length || ""
                                                    }}
                                                    Video
                                                </button>
                                            </div>
                                        </div>

                                        <div class="flex flex-wrap gap-1">
                                            <span
                                                v-for="play in period.positions[
                                                    pos
                                                ].plays"
                                                :key="play.play_id"
                                                class="
                                                    text-[9px]
                                                    bg-blue-50
                                                    text-blue-600
                                                    px-1
                                                    rounded
                                                    border border-blue-100
                                                "
                                                >{{ play.play_name }}</span
                                            >
                                            <span
                                                v-for="video in period
                                                    .positions[pos].videos"
                                                :key="video.id"
                                                class="
                                                    text-[9px]
                                                    bg-green-50
                                                    text-green-600
                                                    px-1
                                                    rounded
                                                    border border-green-100
                                                "
                                                >{{ video.title }}</span
                                            >
                                        </div>
                                    </div>
                                </td>
                            </template>

                            <!-- Period Notes -->
                            <td
                                class="
                                    px-4
                                    py-2
                                    border-r
                                    align-top
                                    bg-yellow-50/20
                                "
                            >
                                <textarea
                                    v-model="period.periodNotes"
                                    @input="
                                        updatePeriodField(
                                            index,
                                            'periodNotes',
                                            $event.target.value
                                        )
                                    "
                                    @focus="
                                        onFieldFocus(
                                            'period-' + index + '-periodNotes'
                                        )
                                    "
                                    @blur="onFieldBlur"
                                    :style="
                                        fieldFocusMap[
                                            'period-' + index + '-periodNotes'
                                        ]
                                            ? {
                                                  borderColor:
                                                      fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-periodNotes'
                                                      ].color,
                                                  boxShadow:
                                                      '0 0 0 2px ' +
                                                      fieldFocusMap[
                                                          'period-' +
                                                              index +
                                                              '-periodNotes'
                                                      ].color +
                                                      '33',
                                              }
                                            : {}
                                    "
                                    :title="
                                        fieldFocusMap[
                                            'period-' + index + '-periodNotes'
                                        ]
                                            ? fieldFocusMap[
                                                  'period-' +
                                                      index +
                                                      '-periodNotes'
                                              ].name + ' is editing this field'
                                            : ''
                                    "
                                    rows="2"
                                    class="
                                        w-full
                                        text-xs
                                        border-none
                                        bg-transparent
                                        focus:ring-0
                                        resize-none
                                        p-0
                                    "
                                    placeholder="General period notes..."
                                ></textarea>
                            </td>

                            <td class="px-4 py-2 text-center print:hidden">
                                <button
                                    @click="removePeriod(index)"
                                    class="text-red-400 hover:text-red-600"
                                >
                                    <ion-icon name="trash-outline"></ion-icon>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Footer Actions -->
            <div
                class="
                    p-4
                    bg-gray-50
                    border-t border-gray-200
                    flex
                    justify-between
                    items-center
                    print:hidden
                "
            >
                <button
                    @click="addPeriod"
                    class="
                        flex
                        items-center
                        gap-2
                        bg-primary
                        text-white
                        px-4
                        py-2
                        rounded-md
                        shadow-sm
                        hover:bg-primary-dark
                        transition-colors
                        font-bold
                    "
                >
                    <ion-icon
                        name="add-circle-outline"
                        class="text-xl"
                    ></ion-icon>
                    Add Period
                </button>
                <div class="flex gap-4">
                    <button
                        @click="savePlanAsNew"
                        v-if="currentPlanId"
                        class="
                            bg-blue-600
                            text-white
                            px-6
                            py-2
                            rounded-md
                            shadow-sm
                            hover:bg-blue-700
                            transition-colors
                            font-bold
                        "
                    >
                        Save As New
                    </button>
                    <button
                        @click="savePlan"
                        class="
                            bg-green-600
                            text-white
                            px-6
                            py-2
                            rounded-md
                            shadow-sm
                            hover:bg-green-700
                            transition-colors
                            font-bold
                        "
                    >
                        Save Practice Plan
                    </button>
                </div>
            </div>
        </div>

        <!-- Modals for Selectors -->
        <PracticePlaySelector
            v-if="activePlaySelector"
            :playbook-api="playbookApi"
            :initial-selected="
                activePlaySelector.pos === 'allPositions'
                    ? periods[activePlaySelector.periodIndex].allPositions.plays
                    : periods[activePlaySelector.periodIndex].positions[
                          activePlaySelector.pos
                      ].plays
            "
            @close="activePlaySelector = null"
            @selected="handlePlaySelected"
        />

        <PracticeVideoSelector
            v-if="activeVideoSelector"
            :video-api="videoApi"
            :initial-selected="
                activeVideoSelector.pos === 'allPositions'
                    ? periods[activeVideoSelector.periodIndex].allPositions
                          .videos
                    : activeVideoSelector.pos === 'splitPositions'
                    ? periods[activeVideoSelector.periodIndex].splitPositions[
                          activeVideoSelector.group
                      ].videos
                    : periods[activeVideoSelector.periodIndex].positions[
                          activeVideoSelector.pos
                      ].videos
            "
            @close="activeVideoSelector = null"
            @selected="handleVideoSelected"
        />

        <ModalCustom
            :is-open="drillModal.isOpen"
            z-index="1001"
            @close="drillModal.isOpen = false"
            title="Custom Drill"
        >
            <div class="w-full min-w-[300px] p-2">
                <input
                    v-model="drillModal.drillName"
                    type="text"
                    class="
                        text-base
                        bg-gray-100
                        py-3
                        px-5
                        font-sans
                        rounded-md
                        block
                        w-full
                        hover:border-gray-300
                        focus:border-primary
                    "
                    placeholder="Enter drill name..."
                    @keyup.enter="confirmNewDrill"
                    autofocus
                />
                <div class="mt-6 flex items-center justify-end gap-2">
                    <button
                        @click="drillModal.isOpen = false"
                        class="
                            px-4
                            py-2
                            text-sm
                            font-medium
                            text-gray-700
                            hover:bg-gray-100
                            rounded-md
                        "
                    >
                        Cancel
                    </button>
                    <button
                        @click="confirmNewDrill"
                        class="
                            px-4
                            py-2
                            text-sm
                            font-medium
                            bg-primary
                            text-white
                            hover:bg-primary-dark
                            rounded-md
                        "
                    >
                        Add Drill
                    </button>
                </div>
            </div>
        </ModalCustom>

        <MiniModalCustom
            :is-open="notification.isOpen"
            @close="notification.isOpen = false"
            z-index="1001"
        >
            <div class="mt-4 py-4 text-center">
                <p class="text-md text-gray-600">{{ notification.message }}</p>
                <div class="mt-6">
                    <button
                        @click="notification.isOpen = false"
                        class="
                            px-6
                            py-2
                            bg-primary
                            text-white
                            rounded-md
                            hover:bg-primary-dark
                            transition-colors
                            font-bold
                            text-sm
                        "
                    >
                        OK
                    </button>
                </div>
            </div>
        </MiniModalCustom>

        <ModalCustom
            :is-open="deleteConfirm.isOpen"
            @close="deleteConfirm.isOpen = false"
            title="Deprecate Practice Plan"
            z-index="1001"
        >
            <div class="mt-4 py-4 text-center">
                <p class="text-md text-gray-600">
                    Are you sure you want to deprecate this practice plan?<br />
                    This will also remove the synced team calendar event.
                </p>
                <div class="mt-8 flex items-center gap-4 justify-end">
                    <button
                        @click="deleteConfirm.isOpen = false"
                        class="
                            bg-gray-200
                            py-2
                            px-6
                            rounded-full
                            font-sans
                            hover:bg-gray-300
                            active:bg-black active:text-white
                            disabled:opacity-25
                            border border-accent
                            text-accent
                            !bg-white
                        "
                        type="button"
                    >
                        Cancel
                    </button>

                    <button
                        @click="executeDelete"
                        class="
                            px-6
                            py-2
                            bg-red-600
                            text-white
                            rounded-full
                            hover:bg-red-700
                            transition-colors
                            font-bold
                        "
                    >
                        Deprecate
                    </button>
                </div>
            </div>
        </ModalCustom>

        <!-- Restore Deprecated Plans Modal -->
        <MiniModalCustom
            :is-open="showDeprecatedModal"
            @close="showDeprecatedModal = false"
            z-index="1001"
        >
            <div class="w-full min-w-[400px] p-4">
                <h3 class="text-lg font-bold mb-4">Restore Deprecated Plans</h3>
                <div
                    v-if="deprecatedPlans.length === 0"
                    class="text-center text-gray-500 py-4 italic"
                >
                    No deprecated plans found
                </div>
                <ul v-else class="divide-y max-h-[400px] overflow-y-auto">
                    <li
                        v-for="p in deprecatedPlans"
                        :key="p.id"
                        class="py-3 flex justify-between items-center"
                    >
                        <div class="text-sm">
                            <span class="font-bold"
                                >#{{ p.practice_number }}</span
                            >
                            <span class="ml-2">{{
                                p.name || getLocalDateString(p.date)
                            }}</span>
                        </div>
                        <button
                            @click="restorePlan(p.id)"
                            class="
                                text-xs
                                bg-primary
                                text-white
                                px-3
                                py-1
                                rounded
                                hover:bg-primary-dark
                                transition-colors
                            "
                        >
                            Restore
                        </button>
                    </li>
                </ul>
            </div>
        </MiniModalCustom>
    </div>
    <div class="mx-auto text-center py-12" v-else>
        <ion-icon
            name="lock-closed-outline"
            class="text-6xl text-gray-300 mb-4"
        ></ion-icon>
        <h2 class="text-2xl font-bold text-gray-700">Access Denied</h2>
        <p class="text-gray-500 mt-2">
            You do not have the required tags to view Practice Plans.
        </p>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import PracticePlaySelector from "./PracticePlaySelector.vue";
import PracticeVideoSelector from "./PracticeVideoSelector.vue";
import ModalCustom from "../ModalCustom.vue";

const props = defineProps({
    team: Object,
    availableTags: { type: Array, default: () => [] },
    initialPlans: { type: Array, default: () => [] },
    deprecatedPlans: { type: Array, default: () => [] },
    hasAccess: { type: Boolean, default: true },
    canManageAccess: { type: Boolean, default: false },
    currentAccessTags: { type: Array, default: () => [] },
    currentUserName: { type: String, default: "User" },
    websocketUrl: { type: String, default: "ws://localhost" },
    env: { type: String, default: "default" },
});

const playbookApi = "/playbook/list";
const videoApi = "/video/list";
const previewUrl = "/";

const isDark = (color) => {
    if (!color) return false;
    color = color.replace("#", "");
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq < 128;
};

const isAccessControlOpen = ref(false);
const activeTags = ref({});
const planName = ref("");
const deprecatedPlans = ref([]);
const showDeprecatedModal = ref(false);

const globalActiveTags = ref(
    props.availableTags.reduce((acc, tag) => {
        if (
            props.currentAccessTags &&
            props.currentAccessTags.includes(tag.id)
        ) {
            acc[tag.id] = true;
        }
        return acc;
    }, {})
);

const notification = ref({
    isOpen: false,
    title: "",
    message: "",
});

const saveGlobalAccess = async () => {
    const selectedTags = Object.keys(globalActiveTags.value)
        .filter((k) => globalActiveTags.value[k])
        .map(Number);
    try {
        const res = await axios.post(
            "/practice_plans/update-access?teamId=" + props.team.id,
            { tags: selectedTags }
        );
        if (res.data.success) {
            showAlert("Success", "Global access settings updated.");
        }
    } catch (e) {
        console.error(e);
        showAlert("Error", "Failed to update access settings.");
    }
};

const deleteConfirm = ref({
    isOpen: false,
    planId: null,
});

const showAlert = (title, message) => {
    notification.value = {
        isOpen: true,
        title,
        message,
    };
};

const getLocalDateString = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const practiceNumber = ref("1");
const practiceDate = ref(getLocalDateString(new Date()));
const notes = ref("");
const periods = ref([]);
const customDrills = ref([]);
const plansList = ref([]);
const currentPlanId = ref(null);

const currentWeekStart = ref(new Date());

const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return d1.split("T")[0] === d2.split("T")[0];
};

const isToday = (dateString) => {
    if (!dateString) return false;
    return dateString.split("T")[0] === getLocalDateString(new Date());
};

const changeWeek = (offset) => {
    const newDate = new Date(currentWeekStart.value);
    newDate.setDate(newDate.getDate() + offset * 7);
    currentWeekStart.value = newDate;
};

const weekStartDateFormatted = computed(() => {
    return currentWeekStart.value.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
});

const weekDays = computed(() => {
    const days = [];
    const start = new Date(currentWeekStart.value);
    start.setDate(start.getDate() - start.getDay() + 1); // Start on Monday

    for (let i = 0; i < 7; i++) {
        const date = new Date(start);
        date.setDate(start.getDate() + i);
        const dateString = getLocalDateString(date);

        const planForDay = plansList.value.find((p) => {
            if (!p.date) return false;
            return p.date.split("T")[0] === dateString;
        });

        days.push({
            name: date.toLocaleDateString(undefined, { weekday: "short" }),
            dayNum: date.getDate(),
            date: dateString,
            plan: planForDay,
        });
    }
    return days;
});

const createNewPlanForDate = (dateString) => {
    currentPlanId.value = null;
    practiceDate.value = dateString;
    practiceNumber.value = (plansList.value.length + 1).toString();
    notes.value = "";
    periods.value = [];
    activeTags.value = {};
    addPeriod();
};

const onPlanSelect = (event) => {
    const id = event.target.value;
    if (!id) {
        createNewPlanForDate(getLocalDateString(new Date()));
        return;
    }
    const plan = plansList.value.find((p) => p.id == id);
    if (plan) {
        if (currentPlanId.value === null) {
            // Treat as loading existing plan to selected date plan
            const targetDate = practiceDate.value;
            loadPlan(plan);
            currentPlanId.value = null;
            practiceDate.value = targetDate;
        } else {
            loadPlan(plan);
        }
    }
};

const connectedUsers = ref([]);
const fieldFocusMap = ref({});

// Y.js collaboration setup
let ydoc = null;
let provider = null;
let yperiods = null;
let ymeta = null;

const initYjs = (planId) => {
    if (!planId) return;

    if (provider) {
        provider.destroy();
    }
    if (ydoc) {
        ydoc.destroy();
    }

    ydoc = new Y.Doc();

    provider = new WebsocketProvider(
        props.websocketUrl,
        `practiceplan-${props.env}-${planId}`,
        ydoc
    );

    ymeta = ydoc.getMap("meta");
    yperiods = ydoc.getArray("periods");

    ymeta.observe(() => {
        if (ymeta.get("practiceNumber") !== undefined)
            practiceNumber.value = ymeta.get("practiceNumber");
        if (ymeta.get("planName") !== undefined)
            planName.value = ymeta.get("planName");
        if (ymeta.get("practiceDate") !== undefined)
            practiceDate.value = ymeta.get("practiceDate");
        if (ymeta.get("notes") !== undefined) notes.value = ymeta.get("notes");
        if (ymeta.get("customDrills") !== undefined)
            customDrills.value = ymeta.get("customDrills") || [];
    });

    yperiods.observeDeep((events, transaction) => {
        if (transaction.local) return;

        periods.value = yperiods.toArray().map((yper) => {
            return yper.toJSON();
        });
    });

    // Awareness Setup
    const colors = [
        "#E02424",
        "#3F83F8",
        "#0E9F6E",
        "#D03801",
        "#9061F9",
        "#6B7280",
        "#EC4899",
        "#059669",
    ];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    const username = props.currentUserName || "User";

    provider.awareness.setLocalStateField("user", {
        name: username,
        color: randomColor,
    });

    provider.awareness.on("change", () => {
        const states = Array.from(provider.awareness.getStates().values());
        connectedUsers.value = states
            .filter((state) => state.user)
            .map((state) => state.user);

        const focusMap = {};
        Array.from(provider.awareness.getStates().entries()).forEach(
            ([clientId, state]) => {
                if (clientId === provider.awareness.clientID) return;
                if (state.user && state.focus) {
                    focusMap[state.focus.path] = {
                        name: state.user.name,
                        color: state.user.color,
                    };
                }
            }
        );
        fieldFocusMap.value = focusMap;
    });

    provider.on("sync", (isSynced) => {
        if (isSynced) {
            if (yperiods.length === 0 && periods.value.length > 0) {
                ydoc.transact(() => {
                    ymeta.set("practiceNumber", practiceNumber.value);
                    ymeta.set("planName", planName.value);
                    ymeta.set("practiceDate", practiceDate.value);
                    ymeta.set("notes", notes.value);
                    ymeta.set("customDrills", customDrills.value);

                    periods.value.forEach((per) => {
                        const yper = new Y.Map();
                        yper.set("time", per.time || "");
                        yper.set("min", per.min || 0);
                        yper.set("type", per.type || "O INDY");
                        yper.set("periodNotes", per.periodNotes || "");
                        yper.set("positions", per.positions || {});
                        yper.set("allPositions", per.allPositions || {});
                        yper.set("splitPositions", per.splitPositions || {});
                        yperiods.push([yper]);
                    });
                });
            } else if (yperiods.length > 0) {
                practiceNumber.value =
                    ymeta.get("practiceNumber") || practiceNumber.value;
                planName.value = ymeta.get("planName") || planName.value;
                practiceDate.value =
                    ymeta.get("practiceDate") || practiceDate.value;
                notes.value = ymeta.get("notes") || notes.value;
                customDrills.value =
                    ymeta.get("customDrills") || customDrills.value;

                periods.value = yperiods.toArray().map((yper) => {
                    return yper.toJSON();
                });
            }
        }
    });
};

const onFieldFocus = (path) => {
    if (provider) {
        provider.awareness.setLocalStateField("focus", { path });
    }
};

const onFieldBlur = () => {
    if (provider) {
        provider.awareness.setLocalStateField("focus", null);
    }
};

const updatePracticeNumber = (val) => {
    if (ymeta) {
        ymeta.set("practiceNumber", val);
    }
};

const updatePlanName = (val) => {
    if (ymeta) {
        ymeta.set("planName", val);
    }
};

const updatePracticeDate = (val) => {
    if (ymeta) {
        ymeta.set("practiceDate", val);
    }
};

const updateNotes = (val) => {
    if (ymeta) {
        ymeta.set("notes", val);
    }
};

const updatePeriodField = (index, field, value) => {
    if (yperiods && index < yperiods.length) {
        const yper = yperiods.get(index);
        yper.set(field, value);
    }
};

const updatePeriodNestedField = (index, nestedKey, field, value) => {
    if (yperiods && index < yperiods.length) {
        const yper = yperiods.get(index);
        const obj = { ...yper.get(nestedKey) };
        obj[field] = value;
        yper.set(nestedKey, obj);
    }
};

const updatePeriodPositionField = (index, pos, field, value) => {
    if (yperiods && index < yperiods.length) {
        const yper = yperiods.get(index);
        const positions = { ...yper.get("positions") };
        if (!positions[pos]) {
            positions[pos] = { drill: "", content: "", plays: [], videos: [] };
        } else {
            positions[pos] = { ...positions[pos] };
        }
        positions[pos][field] = value;
        yper.set("positions", positions);
    }
};

const updatePeriodSplitField = (index, group, field, value) => {
    if (yperiods && index < yperiods.length) {
        const yper = yperiods.get(index);
        const splitPositions = { ...yper.get("splitPositions") };
        if (!splitPositions[group]) {
            splitPositions[group] = {
                drill: "",
                content: "",
                plays: [],
                videos: [],
            };
        } else {
            splitPositions[group] = { ...splitPositions[group] };
        }
        splitPositions[group][field] = value;
        yper.set("splitPositions", splitPositions);
    }
};

const onPeriodTypeChange = (index, value) => {
    handleTypeChange(index);
    updatePeriodField(index, "type", value);
};

const loadPlan = (plan) => {
    currentPlanId.value = plan.id;
    practiceDate.value = plan.date
        ? plan.date.split("T")[0]
        : getLocalDateString(new Date());
    practiceNumber.value = plan.practice_number || "";
    planName.value = plan.name || "";
    notes.value = plan.notes || "";
    periods.value = plan.periods || [];

    const newTags = {};
    if (plan.tags) {
        plan.tags.forEach((t) => {
            newTags[t.id] = true;
        });
    }
    activeTags.value = newTags;

    initYjs(plan.id);

    window.scrollTo({
        top: document.getElementById("printable-plan").offsetTop - 100,
        behavior: "smooth",
    });
};

const savePlanAsNew = () => {
    currentPlanId.value = null;
    savePlan();
};

const savePlan = async () => {
    const payload = {
        id: currentPlanId.value,
        practice_number: practiceNumber.value,
        date: practiceDate.value,
        name: planName.value,
        notes: notes.value,
        periods: periods.value,
        custom_drills: customDrills.value,
    };

    try {
        const res = await axios.post(
            "/practice_plans?teamId=" + props.team.id,
            payload
        );
        if (res.data.success) {
            plansList.value = res.data.plans;
            deprecatedPlans.value = res.data.deprecatedPlans;
            // Update current loaded
            const updated = plansList.value.find(
                (p) => p.id === res.data.saved_id
            );
            if (updated) loadPlan(updated);
            showAlert("Success", "Practice Plan saved and synced to Calendar!");
        }
    } catch (e) {
        console.error(e);
        showAlert("Error", "Failed to save.");
    }
};

const restorePlan = async (id) => {
    try {
        const res = await axios.post(
            `/practice_plans/${id}/restore?teamId=${props.team.id}`
        );
        if (res.data.success) {
            plansList.value = res.data.plans;
            deprecatedPlans.value = res.data.deprecatedPlans;
            if (deprecatedPlans.value.length === 0) {
                showDeprecatedModal.value = false;
            }
            showAlert("Success", "Plan restored!");
        }
    } catch (e) {
        console.error(e);
        showAlert("Error", "Failed to restore.");
    }
};

const deletePlan = (id) => {
    deleteConfirm.value = {
        isOpen: true,
        planId: id,
    };
};

const executeDelete = async () => {
    const id = deleteConfirm.value.planId;
    if (!id) return;

    try {
        const res = await axios.delete(
            `/practice_plans/${id}?teamId=${props.team.id}`
        );
        if (res.data.success) {
            plansList.value = res.data.plans;
            deprecatedPlans.value = res.data.deprecatedPlans;
            if (currentPlanId.value == id) {
                createNewPlanForDate(getLocalDateString(new Date()));
            }
        }
        deleteConfirm.value.isOpen = false;
    } catch (e) {
        console.error(e);
        showAlert("Error", "Failed to delete.");
        deleteConfirm.value.isOpen = false;
    }
};
onMounted(() => {
    plansList.value = props.initialPlans || [];
    deprecatedPlans.value = props.deprecatedPlans || [];
    const today = new Date();
    currentWeekStart.value = today;

    const urlParams = new URLSearchParams(window.location.search);
    const planIdParam = urlParams.get("planId");

    if (planIdParam) {
        const targetPlan = plansList.value.find((p) => p.id == planIdParam);
        if (targetPlan) {
            loadPlan(targetPlan);
            // Also adjust currentWeekStart to this plan's date so it's visible in the week view
            if (targetPlan.date) {
                currentWeekStart.value = new Date(targetPlan.date);
            }
            return;
        }
    }

    const todayString = getLocalDateString(today);
    const todaysPlan = plansList.value.find(
        (p) => p.date && p.date.split("T")[0] === todayString
    );
    if (todaysPlan) {
        loadPlan(todaysPlan);
    } else {
        createNewPlanForDate(todayString);
    }
});

const printPlan = () => {
    window.print();
};

const exportToExcel = () => {
    let originalTable = document
        .getElementById("printable-plan")
        .querySelector("table");
    if (!originalTable) return;

    let masterTable = document.createElement("table");

    // Add Headers
    const addHeaderRow = (text, colSpan = 10, isTitle = false) => {
        let row = masterTable.insertRow();
        let cell = row.insertCell();
        cell.colSpan = colSpan;
        cell.innerHTML = isTitle
            ? `<b style="font-size:18px;">${text}</b>`
            : `<b>${text}</b>`;
    };

    addHeaderRow(
        `${props.team.name} Practice Schedule (${props.team.sport_type})`,
        10,
        true
    );
    addHeaderRow(`Practice #: ${practiceNumber.value || ""}`);
    addHeaderRow(`Date: ${practiceDate.value || ""}`);
    addHeaderRow(`Notes: ${(notes.value || "").replace(/\n/g, "<br>")}`);

    // Add empty row for spacing
    let spacerRow = masterTable.insertRow();
    spacerRow.insertCell().colSpan = 10;

    let table = originalTable.cloneNode(true);

    // Remove hidden elements
    let hiddenElements = table.querySelectorAll(".print\\:hidden");
    hiddenElements.forEach((el) => el.parentNode.removeChild(el));

    // Convert form elements to text
    let inputs = table.querySelectorAll("input, textarea, select");
    inputs.forEach((input) => {
        let text = document.createTextNode(input.value || "");
        input.parentNode.replaceChild(text, input);
    });

    // Append all rows from this table to the master table
    let rows = table.querySelectorAll("tr");
    rows.forEach((row) => masterTable.appendChild(row.cloneNode(true)));

    let html = masterTable.outerHTML;
    let template =
        '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Plan</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body>{table}</body></html>';
    let fullHtml = template.replace("{table}", html);

    let blob = new Blob([fullHtml], { type: "application/vnd.ms-excel" });
    let url = URL.createObjectURL(blob);

    let link = document.createElement("a");
    link.href = url;
    link.download = `${props.team.name
        .replace(/[^a-z0-9]/gi, "_")
        .toLowerCase()}_practice_plan.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const activePlaySelector = ref(null);
const activeVideoSelector = ref(null);

const drillModal = ref({
    isOpen: false,
    index: null,
    pos: null,
    group: null,
    drillName: "",
});

// Constants for positions based on sport
const footballPositions = ["DL/OL", "LB/RB", "Safety/QB", "CB/WR", "J/H"];
const basketballPositions = ["Guards", "Wings", "Bigs"];
const soccerPositions = [
    "Attackers",
    "Midfielders",
    "Defenders",
    "Goalkeepers",
];
const hockeyPositions = ["Forwards", "Defensemen", "Goalies"];
const lacrossePositions = ["Attack", "Midfield", "Defense", "Goalies"];
const volleyballPositions = ["Setters", "Hitters", "Liberos/DS", "Middles"];
const defaultPositions = ["Group A", "Group B", "Group C"];

const basePeriodTypes = [
    { value: "PRE PRAC", label: "PRE PRAC", layout: "expand" },
    { value: "STRETCH", label: "STRETCH", layout: "expand" },
    { value: "SPEED", label: "SPEED", layout: "expand" },
    { value: "SCREEN", label: "SCREEN", layout: "expand" },
    { value: "COMBO", label: "COMBO", layout: "expand" },
    { value: "COMPETE", label: "COMPETE", layout: "expand" },
    { value: "O TEAM", label: "O TEAM", layout: "expand" },
    { value: "D TEAM", label: "D TEAM", layout: "expand" },
    { value: "SPECIAL TEAMS", label: "SPECIAL TEAMS", layout: "expand" },
    {
        value: "ROA",
        label: "ROA",
        layout: "split",
        splits: [
            ["DL/OL", "LB/RB"],
            ["Safety/QB", "CB/WR", "J/H"],
        ],
    },
    { value: "O INDY", label: "O INDY", layout: "standard" },
    { value: "D INDY", label: "D INDY", layout: "standard" },
    { value: "TEAM CALLS", label: "TEAM CALLS", layout: "standard" },
    { value: "SCRIPT", label: "SCRIPT", layout: "standard" },
];

const basketballPeriodTypes = [
    { value: "PRE PRAC", label: "PRE PRAC", layout: "expand" },
    { value: "STRETCH", label: "STRETCH", layout: "expand" },
    { value: "SPEED", label: "SPEED", layout: "expand" },
    { value: "SCREEN", label: "SCREEN", layout: "expand" },
    { value: "COMBO", label: "COMBO", layout: "expand" },
    { value: "COMPETE", label: "COMPETE", layout: "expand" },
    { value: "O TEAM", label: "O TEAM", layout: "expand" },
    { value: "D TEAM", label: "D TEAM", layout: "expand" },
    { value: "SPECIAL", label: "SPECIAL", layout: "expand" },
    { value: "INDY", label: "D INDY", layout: "expand" },
    { value: "TEAM CALLS", label: "TEAM CALLS", layout: "standard" },
    { value: "SCRIPT", label: "SCRIPT", layout: "standard" },
];

const periodTypesBySport = {
    "american-football": basePeriodTypes,
    basketball: basketballPeriodTypes,
    soccer: basePeriodTypes,
    hockey: basePeriodTypes,
    "field-hockey": basePeriodTypes,
    lacrosse: basePeriodTypes,
    volleyball: basePeriodTypes,
    default: basePeriodTypes,
};

const availablePeriodTypes = computed(() => {
    return (
        periodTypesBySport[props.team.sport_type] ||
        periodTypesBySport["default"]
    );
});

const getPeriodLayout = (typeValue) => {
    const pt = availablePeriodTypes.value.find((p) => p.value === typeValue);
    return pt ? pt.layout : "standard";
};

const getSplitConfig = (typeValue) => {
    const pt = availablePeriodTypes.value.find((p) => p.value === typeValue);
    return pt && pt.splits ? pt.splits : [["Group 1"], ["Group 2"]];
};

const getSplitLabels = (typeValue) => {
    const config = getSplitConfig(typeValue);
    return {
        group1: config[0].join(" / "),
        group2: config[1].join(" / "),
    };
};

const getSplitColspans = (typeValue) => {
    const config = getSplitConfig(typeValue);
    return {
        group1: config[0].length,
        group2: config[1].length,
    };
};

const positionColumns = computed(() => {
    switch (props.team.sport_type) {
        case "american-football":
            return footballPositions;
        case "basketball":
            return basketballPositions;
        case "soccer":
            return soccerPositions;
        case "hockey":
        case "field-hockey":
            return hockeyPositions;
        case "lacrosse":
            return lacrossePositions;
        case "volleyball":
            return volleyballPositions;
        default:
            return defaultPositions;
    }
});

const originalFootballDrills = [
    "BALL SECURITY PUNCH",
    "1 STEP DROP FW",
    "STANCE AND START",
    "LOAD STEP",
    "STANCE & START (DIFFERENT STANCES)",
    "TRIANGLE READS",
    "SCOOCH STANCE AND START",
    "GRID",
    "2 TO 1 WORK",
    "STUBBLE BUM",
    "3 STEP DROP FW",
    "4 CONES DRILL",
    "FIT AND DRIVE",
    "ATTACK/REACT",
    "PUSH 2 TO 1 (3-2-1)",
    "ALLEY FIT RUNS",
    "MAN PUNCH",
    "BLITZ EDGE WORK",
    "TRAP PAD JUMP CUT",
    "PLAY ACTION FW",
    "STEM",
    "SPLIT",
    "REACT/ATTACK",
    "PRESS & PUNCH GAP FIT",
    "EYE DISCIPLINE (BLUE, WHITE BROWN)",
    "NO DROP WITH BAG",
    "IZ 2 BAG READ",
    "SPRINT OUT FW",
    "STACK",
    "WRAP",
    "HEAVY TECHNIQUE",
    "OVER/UNDER FITS",
    "BLUE - POST CORNER DRILL",
    "WHITE FOOTWORK",
    "SKIP PULL MECHANICS",
    "WEEZY 2 BAG READ",
    "PRESS RELEASE",
    "KICK",
    "PUSH PULL RIP",
    "SPILL OVERLAP FITS",
    "CATCH PRESS MAN",
    "BAIL FOOTWORK",
    "COUNTER PULL MECH",
    "PASS PRO FIT",
    "CLOUD RELEASE",
    "WZ 2 ON 2 WITH T",
    "DOUBLE HAND SWIPE",
    "GREEN HOOK/CURL DROPS",
    "PEDAL WEAVE MAN",
    "45 DEGREE BREAK",
    "INSIDE ZONE",
    "PASS PRO WIDE BASE",
    "ELBOW SEPARATION",
    "TE SCREEN FOOTWORK",
    "BULL RUSH",
    "#3 DROPPER",
    "COVER 3 ROLL TIMING",
    "90 DEGREE BREAK",
    "IZ 1 ON 1",
    "SCREEN FOOTWORK",
    "LOW BALL CATCH & SHOW",
    "SPEED RUSH",
    "IN PHASE/OUT PHASE",
    "135 DEGREE BREAK",
    "IZ 2 ON 1",
    "MDM Drill",
    "HANDS ON STACK/HEAVY",
    "IN PHASE",
    "IZ 2 ON 2",
    "ENGAGE LEVERAGE DRILL",
    "OUT OF PHASE",
    "WIDE ZONE",
    "CATCH IN TRAFFIC 90 DRILL",
    "ZONE TURN IN PHASE",
    "WZ GET OFF",
    "ZONE TURN OUT OF PHASE",
    "WZ 1 ON 1 TAKE OVER",
    "WZ FIT AND DRIVE",
    "WZ 1 ON 1 COV/UC",
    "WZ 2 ON 1",
    "WZ 2 ON 2",
    "WZ 3 ON 3",
    "GAP SCHEME",
    "GAP SCHEME 2 ON 2",
    "ANGLE DRIVE BLOCK",
    "PASS PRO",
    "PASS SET RESET TO CONE",
    "PASS SET HAND PUNCH",
    "PASS SET HAND PLACE",
    "PASS SET TEMPO",
    "SET POST STEP REACT",
    "HALF SLIDE 3 ON 3",
    "POP SET",
];

const originalBasketballDrills = [
    "Layup Drill",
    "3-Man Weave",
    "Shooting Drills",
    "Defensive Sliding",
    "Box Out Drill",
    "Rebounding",
    "Fast Break",
    "Pick and Roll",
    "Full Court Press",
    "Zone Defense Basics",
    "Mikan Drill",
    "Zig Zag Dribbling",
];

const originalSoccerDrills = [
    "Rondos",
    "Passing Patterns",
    "Shooting Drills",
    "1v1 Defending",
    "2v1 Attacking",
    "Crossing & Finishing",
    "Set Pieces",
    "Goalkeeper Shot Stopping",
    "Small Sided Games",
    "Possession Grid",
];

const originalHockeyDrills = [
    "Skating Warmup",
    "Puck Handling",
    "Passing Drills",
    "Shooting Drills",
    "1v1s",
    "2v1s",
    "Breakout Patterns",
    "Power Play Setup",
    "Penalty Kill Structure",
    "Goalie Angles",
];

const originalFieldHockeyDrills = [
    "Dribbling Course",
    "Push Passing",
    "Hitting/Shooting",
    "Tackling Drills",
    "Penalty Corners",
    "1v1s",
    "Small Sided Games",
    "Goalkeeper Kicking/Clearing",
];

const originalLacrosseDrills = [
    "Line Drills",
    "Ground Balls",
    "Passing & Catching",
    "Shooting Drills",
    "1v1s",
    "Clearing Patterns",
    "Riding Drills",
    "Faceoffs / Draws",
    "Fast Breaks",
    "Man Up / Man Down",
];

const originalVolleyballDrills = [
    "Serving Accuracy",
    "Serve Receive",
    "Passing (Bumping)",
    "Setting Drills",
    "Hitting Lines",
    "Blocking Drills",
    "Digging Drills",
    "Transition Drills",
    "Scrimmage 6v6",
    "Wash Drills",
];

const defaultDrills = [
    "Warmup",
    "Skill Work",
    "Conditioning",
    "Team Strategy",
    "Scrimmage",
];

const availableDrills = computed(() => {
    let original = [];
    switch (props.team.sport_type) {
        case "american-football":
            original = originalFootballDrills;
            break;
        case "basketball":
            original = originalBasketballDrills;
            break;
        case "soccer":
            original = originalSoccerDrills;
            break;
        case "hockey":
            original = originalHockeyDrills;
            break;
        case "field-hockey":
            original = originalFieldHockeyDrills;
            break;
        case "lacrosse":
            original = originalLacrosseDrills;
            break;
        case "volleyball":
            original = originalVolleyballDrills;
            break;
        default:
            original = defaultDrills;
            break;
    }
    return [...customDrills.value, ...original];
});

const addPeriod = () => {
    const newPeriod = {
        time: "",
        min: 5,
        type: "O INDY",
        periodNotes: "",
        positions: {},
        allPositions: {
            drill: "",
            content: "",
            plays: [],
            videos: [],
        },
        splitPositions: {
            group1: { drill: "", content: "", plays: [], videos: [] },
            group2: { drill: "", content: "", plays: [], videos: [] },
        },
    };
    positionColumns.value.forEach((pos) => {
        newPeriod.positions[pos] = {
            drill: "",
            content: "",
            plays: [],
            videos: [],
        };
    });
    periods.value.push(newPeriod);

    if (yperiods) {
        const yper = new Y.Map();
        yper.set("time", newPeriod.time);
        yper.set("min", newPeriod.min);
        yper.set("type", newPeriod.type);
        yper.set("periodNotes", newPeriod.periodNotes);
        yper.set("positions", newPeriod.positions);
        yper.set("allPositions", newPeriod.allPositions);
        yper.set("splitPositions", newPeriod.splitPositions);
        yperiods.push([yper]);
    }
};

const removePeriod = (index) => {
    periods.value.splice(index, 1);
    if (yperiods && index < yperiods.length) {
        yperiods.delete(index, 1);
    }
};

const handleTypeChange = (index) => {
    // Reset or adjust if needed when type changes
};

const promptNewDrill = (index, pos, group = null) => {
    drillModal.value = {
        isOpen: true,
        index,
        pos,
        group,
        drillName: "",
    };
};

const confirmNewDrill = () => {
    const { index, pos, group, drillName } = drillModal.value;
    if (drillName && !availableDrills.value.includes(drillName)) {
        customDrills.value.unshift(drillName);
        if (ymeta) {
            ymeta.set("customDrills", customDrills.value);
        }
    }
    if (drillName && pos) {
        if (pos === "allPositions") {
            periods.value[index].allPositions.drill = drillName;
            updatePeriodNestedField(index, "allPositions", "drill", drillName);
        } else if (pos === "splitPositions" && group) {
            periods.value[index].splitPositions[group].drill = drillName;
            updatePeriodSplitField(index, group, "drill", drillName);
        } else {
            periods.value[index].positions[pos].drill = drillName;
            updatePeriodPositionField(index, pos, "drill", drillName);
        }
    }
    drillModal.value.isOpen = false;
};

const openPlaySelector = (periodIndex, pos, group = null) => {
    activePlaySelector.value = { periodIndex, pos, group };
};

const openVideoSelector = (periodIndex, pos, group = null) => {
    activeVideoSelector.value = { periodIndex, pos, group };
};

const handlePlaySelected = (selectedPlays) => {
    if (activePlaySelector.value) {
        const { periodIndex, pos, group } = activePlaySelector.value;
        if (pos === "allPositions") {
            periods.value[periodIndex].allPositions.plays = selectedPlays;
            updatePeriodNestedField(
                periodIndex,
                "allPositions",
                "plays",
                selectedPlays
            );
        } else if (pos === "splitPositions" && group) {
            periods.value[periodIndex].splitPositions[group].plays =
                selectedPlays;
            updatePeriodSplitField(periodIndex, group, "plays", selectedPlays);
        } else {
            periods.value[periodIndex].positions[pos].plays = selectedPlays;
            updatePeriodPositionField(periodIndex, pos, "plays", selectedPlays);
        }
        activePlaySelector.value = null;
    }
};

const handleVideoSelected = (selectedVideos) => {
    if (activeVideoSelector.value) {
        const { periodIndex, pos, group } = activeVideoSelector.value;
        if (pos === "allPositions") {
            periods.value[periodIndex].allPositions.videos = selectedVideos;
            updatePeriodNestedField(
                periodIndex,
                "allPositions",
                "videos",
                selectedVideos
            );
        } else if (pos === "splitPositions" && group) {
            periods.value[periodIndex].splitPositions[group].videos =
                selectedVideos;
            updatePeriodSplitField(
                periodIndex,
                group,
                "videos",
                selectedVideos
            );
        } else {
            periods.value[periodIndex].positions[pos].videos = selectedVideos;
            updatePeriodPositionField(
                periodIndex,
                pos,
                "videos",
                selectedVideos
            );
        }
        activeVideoSelector.value = null;
    }
};
</script>

<style scoped>
.bg-primary {
    background-color: #1b6fc3;
}
.bg-primary-dark {
    background-color: #155594;
}
.text-primary {
    color: #1b6fc3;
}
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
</style>

<style>
@media print {
    body * {
        visibility: hidden;
    }
    #printable-plan,
    #printable-plan * {
        visibility: visible;
    }
    #printable-plan {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        box-shadow: none;
        border: none;
    }
    @page {
        size: landscape;
        margin: 1cm;
    }
    /* Hide placeholders */
    ::placeholder {
        color: transparent !important;
    }
    /* Remove borders from inputs and textareas */
    input,
    textarea {
        border: none !important;
        box-shadow: none !important;
        resize: none !important;
        background: transparent !important;
        outline: none !important;
    }
    /* Hide select dropdown arrows and borders */
    select {
        appearance: none !important;
        -webkit-appearance: none !important;
        -moz-appearance: none !important;
        background-image: none !important;
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
        outline: none !important;
    }
}
</style>
